import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import type { CashMovement, SessionReport } from '../types'

/**
 * Le rapport Z d'un poste : ce qu'on lit en fermant, et ce qu'on relit le
 * lendemain quand un écart est contesté.
 *
 * Deux blocs, volontairement séparés parce qu'on les confond toujours :
 *
 * * **le tiroir** — fond, ventes en espèces, sorties, apports, théorique,
 *   compté, écart. C'est ce qu'on compte à la main ;
 * * **ce que le poste a encaissé**, tous moyens confondus. La carte et PayPal
 *   y figurent sans jamais être entrés dans le tiroir : les additionner au
 *   théorique fabriquerait un écart à chaque fermeture.
 *
 * Partagé entre le poste de vente (le caissier lit son propre Z en fermant)
 * et l'administration (le trésorier lit ceux de toute l'équipe).
 */
export function ZReport({
  report,
  money,
  className,
}: {
  report: SessionReport
  money: (minor: number) => string
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const closed = report.status === 'closed'
  const variance = report.variance_minor ?? 0
  const time = (value: string | null) =>
    value
      ? new Date(value).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
      : '—'

  const drawer: [string, number][] = [
    [t('pos.z.float'), report.opening_float_minor],
    [t('pos.z.cashSales'), report.sales_minor],
    [t('pos.z.refunds'), report.refunds_minor],
    [t('pos.z.deposits'), report.deposits_minor],
    [t('pos.z.payouts'), report.payouts_minor],
    [t('pos.z.corrections'), report.corrections_minor],
    // Les espèces des serveurs : ni une vente du poste, ni un apport anonyme.
    [t('pos.z.handovers'), report.handovers_minor ?? 0],
  ]
  const methods = Object.entries(report.by_method ?? {}).sort((a, b) => b[1] - a[1])

  return (
    <div className={cn('grid gap-5 sm:grid-cols-2', className)}>
      <div className="min-w-0">
        <p className="overline text-content-muted">{t('pos.z.drawer')}</p>
        <dl className="mt-2 space-y-1.5 text-sm">
          {drawer
            .filter(([, amount], index) => index < 2 || amount !== 0)
            .map(([label, amount]) => (
              <div key={label} className="flex justify-between gap-4">
                <dt className="text-content-secondary">{label}</dt>
                <dd className="tabular-nums text-content-primary">{money(amount)}</dd>
              </div>
            ))}
          <div className="flex justify-between gap-4 border-t border-line-subtle pt-1.5">
            <dt className="text-content-secondary">{t('pos.expected')}</dt>
            <dd className="font-medium tabular-nums text-content-primary">
              {money(report.expected_minor)}
            </dd>
          </div>
          {closed ? (
            <>
              <div className="flex justify-between gap-4">
                <dt className="text-content-secondary">{t('pos.z.counted')}</dt>
                <dd className="tabular-nums text-content-primary">
                  {money(report.counted_minor ?? 0)}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-content-secondary">{t('pos.z.variance')}</dt>
                <dd
                  className={cn(
                    'font-medium tabular-nums',
                    variance === 0
                      ? 'text-state-success'
                      : variance < 0
                        ? 'text-state-danger'
                        : 'text-ember-300',
                  )}
                >
                  {money(variance)}
                </dd>
              </div>
            </>
          ) : null}
        </dl>
        {report.closing_note ? (
          <p className="mt-2 text-caption text-content-secondary">« {report.closing_note} »</p>
        ) : null}
      </div>

      <div className="min-w-0">
        <p className="overline text-content-muted">{t('pos.z.takings')}</p>
        <dl className="mt-2 space-y-1.5 text-sm">
          {methods.length === 0 ? (
            <p className="text-content-muted">{t('pos.z.nothingYet')}</p>
          ) : (
            methods.map(([method, amount]) => (
              <div key={method} className="flex justify-between gap-4">
                <dt className="text-content-secondary">
                  {t(`pos.methods.${method}`, { defaultValue: method })}
                </dt>
                <dd className="tabular-nums text-content-primary">{money(amount)}</dd>
              </div>
            ))
          )}
          <div className="flex justify-between gap-4 border-t border-line-subtle pt-1.5">
            <dt className="text-content-secondary">{t('pos.z.total')}</dt>
            <dd className="font-medium tabular-nums text-champagne-300">
              {money(report.revenue_minor ?? 0)}
            </dd>
          </div>
        </dl>
        <p className="mt-2 text-caption text-content-muted">
          {t('pos.z.counts', { orders: report.orders, entries: report.door_entries ?? 0 })}
          {' · '}
          {time(report.opened_at)} → {closed ? time(report.closed_at) : '…'}
        </p>
        {report.closed_by_label && report.closed_by_label !== report.user_label ? (
          <p className="mt-1 text-caption text-ember-300">
            {t('pos.z.closedBy', { name: report.closed_by_label })}
          </p>
        ) : null}
      </div>
    </div>
  )
}

const KINDS = ['deposit', 'payout', 'correction'] as const
type ManualKind = (typeof KINDS)[number]

/**
 * Le tiroir en cours de service : ce qui y est passé, et ce qu'on y ajoute.
 *
 * Un fond d'appoint qui arrive, des glaçons payés en liquide, une erreur de
 * frappe à rattraper : sans cet écran, ces gestes devenaient un écart
 * inexplicable à la fermeture. Le sens d'un apport ou d'une sortie vient du
 * bouton choisi — on tape un montant, jamais un signe.
 */
export function DrawerPanel({
  movements,
  money,
  busy,
  error,
  onAdd,
}: {
  movements: CashMovement[]
  money: (minor: number) => string
  busy: boolean
  error: string | null
  onAdd: (
    payload: { kind: ManualKind; amount_minor: number; note: string | null },
    done: () => void,
  ) => void
}) {
  const { t, i18n } = useTranslation()
  const [kind, setKind] = useState<ManualKind>('deposit')
  const [amount, setAmount] = useState('')
  const [direction, setDirection] = useState<1 | -1>(-1)
  const [note, setNote] = useState('')

  const value = Number.parseFloat(amount.replace(',', '.'))
  const minor = Number.isFinite(value) && value > 0 ? Math.round(value * 100) : null
  const needsNote = kind !== 'deposit'

  return (
    <Card className="mt-6">
      <p className="overline text-content-muted">{t('pos.drawer.title')}</p>
      <p className="mt-1 text-sm text-content-secondary">{t('pos.drawer.hint')}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {KINDS.map((option) => (
          <Button
            key={option}
            size="sm"
            variant={kind === option ? 'champagne' : 'outline'}
            onClick={() => setKind(option)}
          >
            {t(`pos.drawer.kinds.${option}`)}
          </Button>
        ))}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-[10rem_1fr_auto] sm:items-end">
        <label className="block text-caption text-content-muted">
          {t('pos.drawer.amount')}
          <input
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="mt-1 h-11 w-full rounded-md border border-line bg-bg-base px-3 text-content-primary"
          />
        </label>
        <label className="block text-caption text-content-muted">
          {needsNote ? t('pos.drawer.noteRequired') : t('pos.drawer.note')}
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t(`pos.drawer.placeholder.${kind}`)}
            className="mt-1 h-11 w-full rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary"
          />
        </label>
        <Button
          size="md"
          disabled={busy || minor === null || (needsNote && note.trim().length < 2)}
          onClick={() =>
            onAdd(
              {
                kind,
                amount_minor: kind === 'correction' ? (minor ?? 0) * direction : (minor ?? 0),
                note: note.trim() || null,
              },
              () => {
                setAmount('')
                setNote('')
              },
            )
          }
        >
          {t('pos.drawer.add')}
        </Button>
      </div>

      {kind === 'correction' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {([1, -1] as const).map((sign) => (
            <Button
              key={sign}
              size="sm"
              variant={direction === sign ? 'champagne' : 'ghost'}
              onClick={() => setDirection(sign)}
            >
              {sign > 0 ? t('pos.drawer.more') : t('pos.drawer.less')}
            </Button>
          ))}
        </div>
      ) : null}

      {error ? <p className="mt-2 text-sm text-state-danger">{error}</p> : null}

      {movements.length > 0 ? (
        <ul className="mt-5 divide-y divide-line-subtle border-t border-line-subtle">
          {movements.map((row) => (
            <li key={row.id} className="flex items-baseline justify-between gap-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="text-content-primary">{t(`pos.drawer.kinds.${row.kind}`)}</span>
                {row.note ? (
                  <span className="block truncate text-caption text-content-muted">{row.note}</span>
                ) : null}
              </span>
              <span className="shrink-0 text-right">
                <span
                  className={cn(
                    'block tabular-nums',
                    row.amount_minor < 0 ? 'text-state-danger' : 'text-content-primary',
                  )}
                >
                  {money(row.amount_minor)}
                </span>
                <span className="block text-caption text-content-muted">
                  {new Date(row.created_at).toLocaleTimeString(i18n.language, {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  )
}

/** L'état d'une session, en un badge. */
export function SessionBadge({ status }: { status: SessionReport['status'] }) {
  const { t } = useTranslation()
  return (
    <Badge tone={status === 'open' ? 'success' : 'neutral'}>
      {t(status === 'open' ? 'pos.z.open' : 'pos.z.closed')}
    </Badge>
  )
}
